// components/ui/ShareItinerary.tsx
"use client";
import React, { useState } from "react";
import { Check, Link as LinkIcon } from "lucide-react";
import clsx from "clsx";

interface ShareItineraryProps {
  city: string;
  itinid: string;
}

export default function ShareItinerary({ city, itinid }: ShareItineraryProps) {
  const [copied, setCopied] = useState(false);

  const messageToSend = `Check out this Amazing Trip Plan! - https://www.aitripmaker.com/show/${encodeURIComponent(
    city.toLowerCase().trim()
  )}${itinid !== "DEFAULT" ? `/${itinid}` : ""}`;
  const urlmessage = `https://www.aitripmaker.com/show/${encodeURIComponent(
    city.toLowerCase().trim()
  )}${itinid !== "DEFAULT" ? `/${itinid}` : ""}`;

  const whatsappUrl = `https://wa.me/?text=${messageToSend}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${messageToSend}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${urlmessage}`;
  const linkedinUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${urlmessage}&title=Check out this Amazing Trip Plan`;

  const handleCopyLink = () => {
    // Function to check if a string is already encoded
    const isAlreadyEncoded = (str: string) => {
      return decodeURIComponent(str) !== str;
    };

    // City name (e.g., "kuala lumpur") should be encoded if not already encoded
    const cityEncoded = isAlreadyEncoded(city)
      ? city // If already encoded, leave it as is
      : encodeURIComponent(city.toLowerCase().trim()); // Otherwise, encode it

    const url = `https://www.aitripmaker.com/show/${cityEncoded}${
      itinid !== "DEFAULT" ? `/${itinid}` : ""
    }`;

    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
  };

  const shareButtons = [
    {
      name: "WhatsApp",
      href: whatsappUrl,
      icon: (
        <svg
          className="w-5 h-5"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M16.75 13.96c.25.13.41.2.46.3.06.11.04.61-.21 1.18-.2.56-1.24 1.1-1.7 1.12-.46.02-.47.36-2.96-.73-2.49-1.09-3.99-3.75-4.11-3.92-.12-.17-.96-1.38-.92-2.61.05-1.22.69-1.8.95-2.04.24-.26.51-.29.68-.26h.47c.15 0 .36-.06.55.45l.69 1.87c.06.13.1.28.01.44l-.27.41-.39.42c-.12.12-.26.25-.12.5.12.26.62 1.09 1.32 1.78.91.88 1.71 1.17 1.95 1.3.24.14.39.12.54-.04l.81-.94c.19-.25.35-.19.58-.11l1.67.88M12 2a10 10 0 0 1 10 10 10 10 0 0 1-10 10c-1.97 0-3.8-.57-5.35-1.55L2 22l1.55-4.65A9.969 9.969 0 0 1 2 12 10 10 0 0 1 12 2m0 2a8 8 0 0 0-8 8c0 1.72.54 3.31 1.46 4.61L4.5 19.5l2.89-.96A7.95 7.95 0 0 0 12 20a8 8 0 0 0 8-8 8 8 0 0 0-8-8z" />
        </svg>
      ),
      hoverColor: "hover:bg-[#25D366]",
      ariaLabel: "Share on WhatsApp",
    },
    {
      name: "Twitter",
      href: twitterUrl,
      icon: (
        <svg
          className="w-5 h-5"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      hoverColor: "hover:bg-[#000000]",
      ariaLabel: "Share on X (Twitter)",
    },
    {
      name: "Facebook",
      href: facebookUrl,
      icon: (
        <svg
          className="w-5 h-5"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
      hoverColor: "hover:bg-[#1877F2]",
      ariaLabel: "Share on Facebook",
    },
    {
      name: "LinkedIn",
      href: linkedinUrl,
      icon: (
        <svg
          className="w-5 h-5"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
      hoverColor: "hover:bg-[#0A66C2]",
      ariaLabel: "Share on LinkedIn",
    },
  ];

  return (
    <div className="flex mt-6 justify-center flex-col sm:flex-row items-center gap-3 sm:gap-4">
      <div className="flex gap-2 md:gap-3">
        {shareButtons.map((button) => (
          <a
            key={button.name}
            href={button.href}
            target="_blank"
            rel="noopener noreferrer"
            className={clsx(
              "h-10 w-10 flex items-center justify-center rounded-full",
              "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-white",
              "shadow-sm hover:shadow-md",
              button.hoverColor,
              "hover:text-white",
              "transform hover:scale-105",
              "transition-all duration-200"
            )}
            aria-label={button.ariaLabel}
          >
            {button.icon}
          </a>
        ))}

        <button
          onClick={handleCopyLink}
          className={clsx(
            "relative h-10 w-10 flex items-center justify-center rounded-full",
            "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-white",
            "shadow-sm hover:shadow-md",
            "hover:bg-gray-700 hover:text-white",
            "transform hover:scale-105",
            "transition-all duration-200 cursor-pointer"
          )}
          aria-label="Copy link"
        >
          {copied ? (
            <Check size={20} strokeWidth={2} />
          ) : (
            <LinkIcon size={20} strokeWidth={2} />
          )}
          {copied && (
            <span className="absolute -bottom-8 text-xs bg-gray-800 text-white px-2 py-1 rounded whitespace-nowrap shadow-lg">
              Copied!
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
